// Copyright 2017 Sorama B.V.

import { autoinject } from "aurelia-framework";
import {
    GenericMeasurement as LocalGenericMeasurement,
    MeasurementPsychoAcousticsProperties,
} from "datatypes/SoramaLocalApi";
import {
    BeamformingProjectionSet,
    BeamformingProjectionSetAnalysisProperties,
    GenericMeasurement as CloudGenericMeasurement,
    CriticalBandSpectrumSet,
    FrequencySpectrumSet,
    HolographyProjection,
    HolographyProjectionAnalysisProperties,
    MetricPsychoAcousticsProperties,
    PsychoAcousticsAnalysisProperties,
    PsychoAcousticsBandsResults,
    PsychoAcousticsOverTimeResults,
    PsychoAcousticsResults,
    SpatialEnergyCurveSet,
    SpatialEnergyCurveSetAnalysisProperties,
    SpatialEnergyCurveSetCurve,
    SpectrumSetAnalysisProperties,
    TimeSignal,
    TimeSignalAnalysisProperties,
} from "datatypes/SoramaWebApi";
import { StringExtensions } from "extensions/StringExtensions";
import { Api } from "libraries/WebApi/Api";
import { IWebRequestHandler } from "libraries/WebRequestHandler/IWebRequestHandler";
import { PsychoAcousticsCacheService } from "services/Cache/PsychoAcousticsCacheService";
import { IBeamformingProcessingService } from "services/SoundProcessing/IBeamformingProcessingService";
import { IHolographyProcessingService } from "services/SoundProcessing/IHolographyProcessingService";
import { ISpectralProcessingService } from "services/SoundProcessing/ISpectralProcessingService";
import { IPsychoAcousticsProcessingService } from "./IPsychoAcousticsProcessingService";

type GenericMeasurement = CloudGenericMeasurement & LocalGenericMeasurement;

@autoinject
export class CloudSoundProcessingService
    implements
        ISpectralProcessingService,
        IBeamformingProcessingService,
        IHolographyProcessingService,
        IPsychoAcousticsProcessingService
{
    private static readonly HolographyCall: string = Api.analysis + "/{0}/Holography/Projection";
    private static readonly SpatialEnergyCurveCall: string = Api.analysis + "/{0}/Holography/SpatialEnergyCurve";

    private webRequestHandler: IWebRequestHandler;

    private readonly psychoAcousticsCache: PsychoAcousticsCacheService;

    constructor(webRequestHandler: IWebRequestHandler, psychoAcousticsCache: PsychoAcousticsCacheService) {
        this.webRequestHandler = webRequestHandler;
        this.psychoAcousticsCache = psychoAcousticsCache;
    }

    public unlockBeamforming(measurement: GenericMeasurement): Promise<void> {
        const request = this.webRequestHandler.createGetRequest<void>(
            Api.analysis + "/" + measurement.id + "/BeamformingProjection"
        );

        return request.send().then((x) => {
            return;
        });
    }

    //#region ISoundProcessingService
    public getTimeSignals(
        measurement: GenericMeasurement,
        properties: TimeSignalAnalysisProperties,
        computationProgressCallback?: (progress: number) => void
    ): Promise<TimeSignal[]> {
        let request = this.webRequestHandler.createPostRequest<TimeSignal[]>(
            Api.analysis + "/" + measurement.id + "/Time" + (computationProgressCallback ? "?withProgress=true" : ""),
            properties
        );

        if (computationProgressCallback) request = request.withProgress(computationProgressCallback);

        return request.send().then((response) => response.content);
    }

    public getSpectrumSet(
        measurement: GenericMeasurement,
        properties: SpectrumSetAnalysisProperties,
        computationProgressCallback?: (progress: number) => void
    ): Promise<FrequencySpectrumSet> {
        let request = this.webRequestHandler.createPostRequest<FrequencySpectrumSet>(
            Api.analysis +
                "/" +
                measurement.id +
                "/Spectrum/Set" +
                (computationProgressCallback ? "?withProgress=true" : ""),
            properties
        );

        if (computationProgressCallback) request = request.withProgress(computationProgressCallback);

        return request.send().then((response) => response.content);
    }

    public getPsychoAcoustics(
        measurement: GenericMeasurement,
        properties: PsychoAcousticsAnalysisProperties,
        computationProgressCallback?: (progress: number) => void,
        abortSignal?: AbortSignal
    ): Promise<PsychoAcousticsResults> {
        // Check cache first
        const cachedResults = this.psychoAcousticsCache.getCachedPsychoAcoustics(measurement, properties);
        if (cachedResults) {
            return Promise.resolve(cachedResults);
        }

        const apiCall = `${Api.analysis}/${measurement.id}/Psychoacoustics`;
        const props = <MeasurementPsychoAcousticsProperties>properties;
        // We have to cast here because the generic measurement from the ac is somewhat different?
        props.measurement = <LocalGenericMeasurement>measurement;

        let request = this.webRequestHandler
            .createPostRequest<PsychoAcousticsResults>(apiCall, props)
            .withBSONResponse();

        if (computationProgressCallback && request.supportsProgressWithCurrentSettings()) {
            request = request.withUrl(apiCall + "?withProgress=true");
            request = request.withProgress(computationProgressCallback);
        }
        return request.send().then((response) => {
            // Guard: skip if no valid data (with safe navigation)
            if (!response?.content?.data) return response.content;

            // Cache the results before returning
            try {
                this.psychoAcousticsCache.cachePsychoAcoustics(measurement, properties, response.content);
            } catch (error) {
                console.warn("Failed to cache results:", error);
            }
            return response.content;
        });
    }

    public getPsychoAcousticMetricOverTime(
        measurement: GenericMeasurement,
        properties: MetricPsychoAcousticsProperties,
        computationProgressCallback?: (progress: number) => void
    ): Promise<PsychoAcousticsOverTimeResults> {
        // Check cache first
        const cachedResults = this.psychoAcousticsCache.getCachedMetricOverTime(measurement, properties);
        if (cachedResults) {
            return Promise.resolve(cachedResults);
        }

        let request = this.webRequestHandler.createPostRequest<PsychoAcousticsOverTimeResults>(
            Api.analysis +
                "/" +
                measurement.id +
                "/Psychoacoustics/Time" +
                (computationProgressCallback ? "?withProgress=true" : ""),
            properties
        );

        if (computationProgressCallback) request = request.withProgress(computationProgressCallback);

        return request.send().then((response) => {
            // Cache the results before returning
            try {
                this.psychoAcousticsCache.cacheMetricOverTime(measurement, properties, response.content);
            } catch (error) {
                console.warn("Failed to cache results:", error);
            }
            return response.content;
        });
    }

    public getPsychoAcousticMetricBands(
        measurement: GenericMeasurement,
        properties: MetricPsychoAcousticsProperties,
        computationProgressCallback?: (progress: number) => void
    ): Promise<PsychoAcousticsBandsResults> {
        let request = this.webRequestHandler.createPostRequest<PsychoAcousticsBandsResults>(
            Api.analysis +
                "/" +
                measurement.id +
                "/Psychoacoustics/CriticalBands" +
                (computationProgressCallback ? "?withProgress=true" : ""),
            properties
        );

        if (computationProgressCallback) request = request.withProgress(computationProgressCallback);

        return request.send().then((response) => response.content);
    }

    public getPsychoAcousticsCriticalBandSet(
        measurement: GenericMeasurement,
        properties: MetricPsychoAcousticsProperties,
        computationProgressCallback?: (progress: number) => void
    ): Promise<CriticalBandSpectrumSet> {
        let request = this.webRequestHandler.createPostRequest<CriticalBandSpectrumSet>(
            Api.analysis +
                "/" +
                measurement.id +
                "/Psychoacoustics/CriticalBandSet" +
                (computationProgressCallback ? "?withProgress=true" : ""),
            properties
        );

        if (computationProgressCallback) request = request.withProgress(computationProgressCallback);

        return request.send().then((response) => response.content);
    }

    public getSensorRMSValues(measurement: GenericMeasurement): Promise<number[]> {
        return this.webRequestHandler
            .createPostRequest<number[]>(Api.analysis + "/" + measurement.id + "/Sensor/RMS")
            .send()
            .then((response) => response.content);
    }

    public getBeamformingProjections(
        measurement: GenericMeasurement,
        properties: BeamformingProjectionSetAnalysisProperties,
        computationProgressCallback?: (progress: number) => void
    ): Promise<BeamformingProjectionSet> {
        let request = this.webRequestHandler.createPostRequest<BeamformingProjectionSet>(
            Api.analysis +
                "/" +
                measurement.id +
                "/BeamformingProjection" +
                (computationProgressCallback ? "?withProgress=true" : ""),
            properties
        );

        if (computationProgressCallback) request = request.withProgress(computationProgressCallback);

        return request.send().then((response) => response.content);
    }

    public getHolographyProjections(
        measurement: GenericMeasurement,
        properties: HolographyProjectionAnalysisProperties,
        computationProgressCallback?: (progress: number) => void
    ): Promise<HolographyProjection[]> {
        let request = this.webRequestHandler.createPostRequest<HolographyProjection[]>(
            StringExtensions.format(CloudSoundProcessingService.HolographyCall, measurement.id) +
                (computationProgressCallback ? "?withProgress=true" : ""),
            properties
        );

        if (computationProgressCallback) request = request.withProgress(computationProgressCallback);

        return request.send().then((response) => response.content);
    }

    public getSpatialEnergyCurve(
        measurement: GenericMeasurement,
        properties: SpatialEnergyCurveSetAnalysisProperties,
        computationProgressCallback?: (progress: number) => void
    ): Promise<SpatialEnergyCurveSetCurve> {
        let request = this.webRequestHandler.createPostRequest<SpatialEnergyCurveSet>(
            StringExtensions.format(CloudSoundProcessingService.SpatialEnergyCurveCall, measurement.id) +
                (computationProgressCallback ? "?withProgress=true" : ""),
            properties
        );

        if (computationProgressCallback) request = request.withProgress(computationProgressCallback);

        return request.send().then((response) => response.content.results[0]);
    }

    public beamformingAnalysisCosts(measurement: GenericMeasurement): Promise<void> {
        const request = this.webRequestHandler.createGetRequest<void>(
            Api.analysis + "/" + measurement.id + "/BeamformingProjection"
        );

        return request.send().then((x) => {
            return;
        });
    }

    public genericAnalysisCosts(measurement: GenericMeasurement): Promise<void> {
        const request = this.webRequestHandler.createGetRequest<void>(
            Api.analysis + "/" + measurement.id + "/GenericAnalysis"
        );

        return request.send().then((x) => {
            return;
        });
    }

    public psychoAcousticsAnalysisCosts(measurement: GenericMeasurement): Promise<void> {
        const request = this.webRequestHandler.createGetRequest<void>(
            Api.analysis + "/" + measurement.id + "/Psychoacoustics"
        );

        return request.send().then((x) => {
            return;
        });
    }

    //#endregion
}
